import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Input,
  Tabs,
  Typography,
  Tooltip,
  message,
  Drawer,
  Descriptions,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { CorrectionTimeline, CorrectionStatusTag } from '../components/shared/CorrectionTimeline';
import type { CompensationStep, CorrectionStatus } from '@shared/types';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CorrectionRecord {
  id: number;
  code: string;
  sourceModule: string;
  sourceEntity: string;
  sourceRecordId: number;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  reason: string;
  status: CorrectionStatus;
  compensationChain: CompensationStep[] | null;
  failureReason: string | null;
  requestedBy: { id: number; name: string };
  approvedBy: { id: number; name: string } | null;
  createdAt: string;
  approvedAt: string | null;
  executedAt: string | null;
}

interface ListResponse {
  data: CorrectionRecord[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function CorrectionQueuePage() {
  const { user } = useAuth();
  const [data, setData] = useState<CorrectionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('pending_approval');
  const [detail, setDetail] = useState<CorrectionRecord | null>(null);
  const [rejectModal, setRejectModal] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const isAdmin = user?.roles.includes('admin');
  const isManager = user?.roles.includes('manager') || isAdmin;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get<ListResponse>('/corrections', { params });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch {
      message.error('Failed to load corrections');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id: number) => {
    setActionLoading(id);
    try {
      await api.post(`/corrections/${id}/approve`);
      message.success('Correction approved');
      fetchData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Approval failed';
      message.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal);
    try {
      await api.post(`/corrections/${rejectModal}/reject`, { reason: rejectReason });
      message.success('Correction rejected');
      setRejectModal(null);
      setRejectReason('');
      fetchData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Rejection failed';
      message.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExecute = async (id: number) => {
    Modal.confirm({
      title: 'Execute Correction?',
      content: 'This will run the compensation chain. Affected records will be modified. Continue?',
      okText: 'Execute',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(id);
        try {
          await api.post(`/corrections/${id}/execute`);
          message.success('Correction executed successfully');
          fetchData();
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Execution failed';
          message.error(msg);
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const columns: ColumnsType<CorrectionRecord> = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 130,
      render: (code: string) => <Text strong style={{ fontFamily: 'monospace' }}>{code}</Text>,
    },
    {
      title: 'Source',
      key: 'source',
      width: 200,
      render: (_, r) => (
        <Text>
          {r.sourceModule}/{r.sourceEntity} <Text type="secondary">#{r.sourceRecordId}</Text>
        </Text>
      ),
    },
    {
      title: 'Field',
      dataIndex: 'field',
      width: 120,
    },
    {
      title: 'Change',
      key: 'change',
      width: 200,
      render: (_, r) => (
        <Text>
          <Text delete type="secondary">{String(r.oldValue)}</Text>
          {' → '}
          <Text strong>{String(r.newValue)}</Text>
        </Text>
      ),
    },
    {
      title: 'Requested By',
      dataIndex: ['requestedBy', 'name'],
      width: 130,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (status: CorrectionStatus) => <CorrectionStatusTag status={status} />,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      width: 150,
      render: (d: string) => new Date(d).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => setDetail(record)}
            />
          </Tooltip>
          {record.status === 'pending_approval' && isManager && (
            <>
              <Tooltip title="Approve">
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={actionLoading === record.id}
                  onClick={() => handleApprove(record.id)}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => setRejectModal(record.id)}
                />
              </Tooltip>
            </>
          )}
          {record.status === 'approved' && isAdmin && (
            <Tooltip title="Execute">
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                loading={actionLoading === record.id}
                onClick={() => handleExecute(record.id)}
                style={{ color: '#000', borderColor: '#000' }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const tabs = [
    { key: 'pending_approval', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'executing', label: 'Executing' },
    { key: 'completed', label: 'Completed' },
    { key: 'failed', label: 'Failed' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Error Corrections</Title>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </div>

      <Card>
        <Tabs
          activeKey={statusFilter}
          onChange={(key) => { setStatusFilter(key); setPage(1); }}
          items={tabs}
        />
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (t) => `${t} corrections`,
            onChange: setPage,
          }}
        />
      </Card>

      {/* Reject Modal */}
      <Modal
        open={rejectModal !== null}
        title="Reject Correction"
        onCancel={() => { setRejectModal(null); setRejectReason(''); }}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading === rejectModal }}
      >
        <TextArea
          rows={3}
          placeholder="Reason for rejection (optional)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.code ?? 'Correction Detail'}
        width={520}
      >
        {detail && (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Status">
                <CorrectionStatusTag status={detail.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {detail.sourceModule}/{detail.sourceEntity} #{detail.sourceRecordId}
              </Descriptions.Item>
              <Descriptions.Item label="Field">{detail.field}</Descriptions.Item>
              <Descriptions.Item label="Old Value">{String(detail.oldValue)}</Descriptions.Item>
              <Descriptions.Item label="New Value">{String(detail.newValue)}</Descriptions.Item>
              <Descriptions.Item label="Reason">{detail.reason}</Descriptions.Item>
              <Descriptions.Item label="Requested By">
                {detail.requestedBy.name} &mdash; {new Date(detail.createdAt).toLocaleString()}
              </Descriptions.Item>
              {detail.approvedBy && (
                <Descriptions.Item label="Approved By">
                  {detail.approvedBy.name} &mdash; {detail.approvedAt && new Date(detail.approvedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              {detail.executedAt && (
                <Descriptions.Item label="Executed At">
                  {new Date(detail.executedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              {detail.failureReason && (
                <Descriptions.Item label="Failure Reason">
                  <Text type="danger">{detail.failureReason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            {detail.compensationChain && detail.compensationChain.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Title level={5}>Compensation Chain</Title>
                <CorrectionTimeline steps={detail.compensationChain} status={detail.status} />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
